import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { attachmentRecords, vendors, purchaseOrders, poSiteLinks } from '../api/methods';
import { inventoryKeys } from './useInventory';
// ── Vendors (#546 Phase 1, po_manage-gated) ──────────────────────────────────
export const vendorKeys = {
    all: ['vendors'],
    list: (params) => ['vendors', 'list', params],
    attachments: (vendorKey) => ['vendors', 'attachments', vendorKey],
};
/** Full-detail vendor directory for the management UI + PO builder picker. */
export function useVendors(params = {}, enabled = true) {
    return useQuery({
        queryKey: vendorKeys.list(params),
        queryFn: () => vendors.list(params).then((r) => r.vendors),
        enabled,
        staleTime: 60_000,
        placeholderData: keepPreviousData,
    });
}
export function useCreateVendor() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (data) => vendors.create(data).then((r) => r.vendor),
        onSuccess: () => qc.invalidateQueries({ queryKey: vendorKeys.all }),
    });
}
export function useUpdateVendor() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => vendors.update(id, data).then((r) => r.vendor),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: vendorKeys.all });
            // Vendor names/terms are denormalized onto PO list/detail rows.
            qc.invalidateQueries({ queryKey: purchaseOrderKeys.all });
        },
    });
}
export function useVendorAttachments(vendorKey, options = {}) {
    const limit = options.limit ?? 50;
    const offset = options.offset ?? 0;
    return useQuery({
        queryKey: [...vendorKeys.attachments(vendorKey ?? ''), { limit, offset }],
        queryFn: () => vendors.getAttachments(vendorKey, { limit, offset }),
        enabled: !!vendorKey,
    });
}
export function useUploadVendorAttachment(vendorKey) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (formData) => vendors.uploadAttachment(vendorKey, formData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: vendorKeys.attachments(vendorKey) });
            queryClient.invalidateQueries({ queryKey: vendorKeys.all });
        },
    });
}
export function useUpdateVendorAttachment(vendorKey) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ attachmentId, data }) => (attachmentRecords.updateCompliance(attachmentId, data)),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: vendorKeys.attachments(vendorKey) }),
    });
}
// ── Purchase orders ──────────────────────────────────────────────────────────
export const purchaseOrderKeys = {
    all: ['purchase-orders'],
    list: (params) => ['purchase-orders', 'list', params],
    qboList: (params) => ['purchase-orders', 'qbo', 'list', params],
    qboAll: ['purchase-orders', 'qbo'],
    qboSiteLinks: (qbPoId) => ['purchase-orders', 'qbo', qbPoId, 'site-links'],
    detail: (id) => ['purchase-orders', 'detail', id],
    appSiteLinks: (id) => ['purchase-orders', id, 'site-links'],
    sitePoLinks: (siteId) => ['sites', String(siteId), 'po-links'],
    itemVendorCosts: (qbItemId) => ['purchase-orders', 'item-vendor-costs', qbItemId],
    attachments: (id) => ['purchase-orders', 'attachments', id],
    receipts: (id) => ['purchase-orders', 'receipts', id],
};
export function usePurchaseOrders(params = {}) {
    return useQuery({
        queryKey: purchaseOrderKeys.list(params),
        queryFn: () => purchaseOrders.list(params),
        staleTime: 15_000,
        placeholderData: keepPreviousData,
    });
}
export function usePurchaseOrder(id, enabled = true) {
    return useQuery({
        queryKey: purchaseOrderKeys.detail(id ?? ''),
        queryFn: () => purchaseOrders.get(id).then((r) => r.purchase_order),
        enabled: enabled && !!id,
        staleTime: 5_000,
    });
}
export function useQboPurchaseOrders(params = {}, enabled = true) {
    return useQuery({
        queryKey: purchaseOrderKeys.qboList(params),
        queryFn: () => purchaseOrders.qboList(params),
        enabled,
        staleTime: 30_000,
        placeholderData: keepPreviousData,
    });
}
export function useSyncQboPurchaseOrders() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => purchaseOrders.qboSync(),
        onSuccess: () => qc.invalidateQueries({ queryKey: purchaseOrderKeys.qboAll }),
    });
}
export function useRefreshQboPurchaseOrder() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (qbPoId) => purchaseOrders.qboRefresh(qbPoId).then((r) => r.purchase_order),
        onSuccess: () => qc.invalidateQueries({ queryKey: purchaseOrderKeys.qboAll }),
    });
}
export function useSitePoLinks(siteId, enabled = true) {
    return useQuery({
        queryKey: purchaseOrderKeys.sitePoLinks(siteId ?? ''),
        queryFn: () => poSiteLinks.listForSite(siteId).then((r) => r.links),
        enabled: enabled && siteId != null && String(siteId) !== '',
        staleTime: 10_000,
    });
}
export function useAppPoSiteLinks(poId, enabled = true) {
    return useQuery({
        queryKey: purchaseOrderKeys.appSiteLinks(poId ?? ''),
        queryFn: () => purchaseOrders.listSiteLinksForAppPo(poId).then((r) => r.links),
        enabled: enabled && !!poId,
        staleTime: 10_000,
    });
}
function invalidatePoSiteLinkQueries(qc, link, extras = {}) {
    const siteId = extras.siteId ?? link?.site_id;
    const qbPoId = extras.qbPoId ?? link?.qb_po_id;
    const poId = extras.poId ?? link?.po_id ?? link?.app_po?.id;
    if (siteId != null)
        qc.invalidateQueries({ queryKey: purchaseOrderKeys.sitePoLinks(siteId) });
    if (qbPoId)
        qc.invalidateQueries({ queryKey: purchaseOrderKeys.qboSiteLinks(qbPoId) });
    if (poId)
        qc.invalidateQueries({ queryKey: purchaseOrderKeys.appSiteLinks(poId) });
}
export function useCreatePoLink() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ siteId, qbPoId, note }) => poSiteLinks.create(siteId, { qb_po_id: qbPoId, note }),
        onSuccess: (res, vars) => {
            invalidatePoSiteLinkQueries(qc, res.link, { siteId: vars.siteId, qbPoId: vars.qbPoId });
        },
    });
}
export function useUpdatePoLinkNote() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, note }) => poSiteLinks.updateNote(id, note).then((r) => r.link),
        onSuccess: (link) => {
            invalidatePoSiteLinkQueries(qc, link);
        },
    });
}
export function useDeletePoLink() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id }) => poSiteLinks.delete(id),
        onSuccess: (_void, vars) => {
            invalidatePoSiteLinkQueries(qc, null, vars);
        },
    });
}
export function useItemVendorCosts(qbItemId, enabled = true) {
    return useQuery({
        queryKey: purchaseOrderKeys.itemVendorCosts(qbItemId ?? ''),
        queryFn: () => purchaseOrders.itemVendorCosts(qbItemId).then((r) => r.costs),
        enabled: enabled && !!qbItemId,
        staleTime: 60_000,
    });
}
export function useCreatePurchaseOrder() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (data) => purchaseOrders.create(data).then((r) => r.purchase_order),
        onSuccess: (po) => {
            qc.setQueryData(purchaseOrderKeys.detail(po.id), po);
            qc.invalidateQueries({ queryKey: purchaseOrderKeys.all });
        },
    });
}
/** Draft-only edit; the server 409s once the PO is sent/cancelled. */
export function useUpdatePurchaseOrder() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => purchaseOrders.update(id, data).then((r) => r.purchase_order),
        onSuccess: (po) => {
            qc.setQueryData(purchaseOrderKeys.detail(po.id), po);
            qc.invalidateQueries({ queryKey: purchaseOrderKeys.all });
        },
    });
}
/** QBO-first edit for a synced PO; preserves receipt-backed local line ids. */
export function useEditSyncedPurchaseOrder() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => purchaseOrders.qboEdit(id, data).then((r) => r.purchase_order),
        onSuccess: (po) => {
            qc.setQueryData(purchaseOrderKeys.detail(po.id), po);
            qc.invalidateQueries({ queryKey: purchaseOrderKeys.all });
            qc.invalidateQueries({ queryKey: purchaseOrderKeys.qboAll });
        },
    });
}
/** draft → sent. Lines lock; the printable PO becomes the delivery artifact. */
export function useSendPurchaseOrder() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => purchaseOrders.send(id).then((r) => r.purchase_order),
        onSuccess: (po) => {
            qc.setQueryData(purchaseOrderKeys.detail(po.id), po);
            qc.invalidateQueries({ queryKey: purchaseOrderKeys.all });
        },
    });
}
export function useSyncPurchaseOrderQboNote() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => purchaseOrders.syncPurchaseOrderQboNote(id).then((r) => r.result),
        onSuccess: (_result, id) => {
            qc.invalidateQueries({ queryKey: purchaseOrderKeys.detail(id) });
            qc.invalidateQueries({ queryKey: purchaseOrderKeys.appSiteLinks(id) });
        },
    });
}
export function useCancelPurchaseOrder() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, reason }) => purchaseOrders.cancel(id, reason).then((r) => r.purchase_order),
        onSuccess: (po) => {
            qc.setQueryData(purchaseOrderKeys.detail(po.id), po);
            qc.invalidateQueries({ queryKey: purchaseOrderKeys.all });
        },
    });
}
/** Receipt history for a PO (po_manage). */
export function usePurchaseOrderReceipts(id, enabled = true) {
    return useQuery({
        queryKey: purchaseOrderKeys.receipts(id ?? ''),
        queryFn: () => purchaseOrders.getReceipts(id).then((r) => r.receipts),
        enabled: enabled && !!id,
        staleTime: 5_000,
    });
}
/**
 * Receive goods against a PO (#546 Phase 2). Refreshes the PO (status
 * auto-updates), its receipts, and on-hand quantities.
 */
export function useReceivePurchaseOrder() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => purchaseOrders.receive(id, data),
        onSuccess: (res) => {
            const po = res.purchase_order;
            qc.setQueryData(purchaseOrderKeys.detail(po.id), po);
            qc.invalidateQueries({ queryKey: purchaseOrderKeys.all });
            qc.invalidateQueries({ queryKey: purchaseOrderKeys.receipts(po.id) });
            // On-hand may have changed for tracked items.
            qc.invalidateQueries({ queryKey: inventoryKeys.all });
        },
    });
}
/** Short-close (sent|partially_received → closed). Reason required. */
export function useClosePurchaseOrder() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, reason }) => purchaseOrders.close(id, reason).then((r) => r.purchase_order),
        onSuccess: (po) => {
            qc.setQueryData(purchaseOrderKeys.detail(po.id), po);
            qc.invalidateQueries({ queryKey: purchaseOrderKeys.all });
        },
    });
}
/** Drafts only. */
export function useDeletePurchaseOrder() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => purchaseOrders.delete(id),
        onSuccess: (_void, id) => {
            qc.removeQueries({ queryKey: purchaseOrderKeys.detail(id) });
            qc.invalidateQueries({ queryKey: purchaseOrderKeys.all });
        },
    });
}
// ── PO attachments ───────────────────────────────────────────────────────────
export function usePurchaseOrderAttachments(id, options = {}) {
    const limit = options.limit ?? 50;
    const offset = options.offset ?? 0;
    return useQuery({
        queryKey: [...purchaseOrderKeys.attachments(id ?? ''), { limit, offset }],
        queryFn: () => purchaseOrders.getAttachments(id, { limit, offset }),
        enabled: !!id,
    });
}
export function useUploadPurchaseOrderAttachment(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (formData) => purchaseOrders.uploadAttachment(id, formData),
        onSuccess: (att) => {
            qc.setQueriesData({ queryKey: purchaseOrderKeys.attachments(id) }, (old) => old ? { ...old, attachments: [att, ...old.attachments], total: old.total + 1 } : old);
            qc.invalidateQueries({ queryKey: purchaseOrderKeys.attachments(id) });
        },
    });
}
export function useDeletePurchaseOrderAttachment(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (attachmentId) => purchaseOrders.deleteAttachment(id, attachmentId),
        onSuccess: (_data, attachmentId) => {
            qc.setQueriesData({ queryKey: purchaseOrderKeys.attachments(id) }, (old) => {
                if (!old)
                    return old;
                const remaining = old.attachments.filter((a) => a.id !== attachmentId);
                return { ...old, attachments: remaining, total: Math.max(0, old.total - (old.attachments.length - remaining.length)) };
            });
            qc.invalidateQueries({ queryKey: purchaseOrderKeys.attachments(id) });
        },
    });
}
//# sourceMappingURL=usePurchaseOrders.js.map